// src/admin/screens/config/SystemConfigScreen.tsx
// Super-admin editor for tunable business rules stored in public.system_config.
// Any admin role can read (RLS permits select); only super_admin (non-readonly)
// sees the Edit action because the Capability `config.update` is role-gated
// in useRole. The Sidebar entry is gated on `config.read` and already wired.
//
// We render with the shipped DataTable for consistency with every other list
// screen (and its loading/empty/error states).
import { useState } from 'react';
import Card from '@admin/components/Card';
import Button from '@admin/components/Button';
import DataTable, { type Column } from '@admin/components/DataTable';
import Skeleton from '@admin/components/Skeleton';
import SectionHead from '@admin/components/SectionHead';
import { useSystemConfig, type ConfigRow } from '@admin/hooks/useSystemConfig';
import { useRole } from '@admin/auth/useRole';
import { formatCatDateTime } from '@admin/utils/dates';
import UpdateConfigModal from './modals/UpdateConfigModal';
import styles from './SystemConfigScreen.module.css';

function renderValue(row: ConfigRow): string {
  if (row.kind === 'text') return String(row.value ?? '');
  if (row.kind === 'int') return String(row.value ?? '');
  try {
    return JSON.stringify(row.value);
  } catch {
    return String(row.value);
  }
}

export default function SystemConfigScreen() {
  const { can } = useRole();
  const { data, isLoading, error, refetch } = useSystemConfig();
  const [editing, setEditing] = useState<ConfigRow | null>(null);
  const rowCount = data?.length ?? 0;

  const columns: Column<ConfigRow>[] = [
    {
      key: 'key',
      header: 'Key',
      render: (r) => <code className={styles.code}>{r.config_key}</code>,
    },
    {
      key: 'value',
      header: 'Value',
      render: (r) => <code className={styles.value}>{renderValue(r)}</code>,
    },
    {
      key: 'description',
      header: 'Description',
      render: (r) => <span className={styles.desc}>{r.description}</span>,
    },
    {
      key: 'updated',
      header: 'Last updated',
      render: (r) => (
        <div className={styles.updated}>
          <span className="mono" style={{ color: 'var(--c-text-muted)' }}>
            {formatCatDateTime(r.updated_at)}
          </span>
          <span className={styles.muted}>
            {r.updated_by?.display_name ?? r.updated_by?.email ?? '—'}
          </span>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '100px',
      render: (r) =>
        can('config.update') ? (
          <Button variant="secondary" size="sm" onClick={() => setEditing(r)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  const closeWith = (refresh: boolean) => {
    setEditing(null);
    if (refresh) void refetch();
  };

  return (
    <div className={styles.page}>
      <SectionHead
        eyebrow="Configuration"
        title="System config"
        description={`${rowCount} tunable rule${rowCount === 1 ? '' : 's'} · changes audit-logged and effective immediately`}
      />

      {isLoading ? (
        <Card>
          <div className={styles.loadingStack}>
            <Skeleton h={16} />
            <Skeleton h={16} />
            <Skeleton h={16} />
            <Skeleton h={16} />
            <Skeleton h={16} />
            <Skeleton h={16} />
            <Skeleton h={16} />
            <Skeleton h={16} />
          </div>
        </Card>
      ) : (
        <DataTable<ConfigRow>
          rows={data ?? []}
          columns={columns}
          isLoading={false}
          error={error ? (error as Error).message : null}
          emptyLabel="No configuration rows."
          rowKey={(r) => r.id}
        />
      )}

      {editing && <UpdateConfigModal row={editing} onClose={closeWith} />}
    </div>
  );
}
